import { Component, ViewChild, ElementRef, Renderer2, inject } from '@angular/core';

@Component({
  selector: 'app-ai-chatbot',
  templateUrl: './ai-chatbot.component.html',
  styleUrl: './ai-chatbot.component.scss'
})
export class AiChatbotComponent {
  @ViewChild('chatPopup') chatPopupRef: ElementRef<HTMLDivElement>;
  @ViewChild('chatIframe') chatIframeRef: ElementRef<HTMLIFrameElement>;
  @ViewChild('chatToggle') chatToggleRef: ElementRef<HTMLButtonElement>;
  @ViewChild('resizeHandle') resizeHandleRef: ElementRef<HTMLDivElement>;

  private isDragging = false;
  private dragOffset = { x: 0, y: 0 };

  private renderer = inject(Renderer2);
  private mouseMoveListener: () => void;
  private mouseUpListener: () => void;
  private touchMoveListener: () => void;
  private touchEndListener: () => void;

  private isResizing = false;
  private resizeStart = { x: 0, y: 0, width: 0, height: 0 };
  private resizeMoveListener: () => void;
  private resizeUpListener: () => void;
  private resizeTouchMoveListener: () => void;
  private resizeTouchEndListener: () => void;


  chatVisible = false;

  ngOnDestroy(): void {
    this.destroyResize();
    this.destroyDrag();

    window.removeEventListener('message', this.handleChatMessage, false);
  }

  ngAfterViewInit(): void {
    // Listen for messages from the chat iframe
    window.addEventListener('message', this.handleChatMessage, false);
  }

  toggleChat(): void {
    this.chatVisible = !this.chatVisible;
  }

  handleChatMessage = (event: MessageEvent) => {
    if (!this.chatPopupRef || !this.chatIframeRef) return;
    if (event.data === 'chatClosed') {
      this.chatVisible = false;
    }
    if (event.data === 'chatReload') {
      const iframe = this.chatIframeRef.nativeElement;
      iframe.src = iframe.src; // Reload iframe
      this.chatVisible = true;
    }
  };

  startDrag(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this.isDragging = true;
    const popup = this.chatPopupRef.nativeElement;
    const clientX = (event instanceof TouchEvent) ? event.touches[0].clientX : event.clientX;
    const clientY = (event instanceof TouchEvent) ? event.touches[0].clientY : event.clientY;
    this.dragOffset = {
      x: clientX - popup.offsetLeft,
      y: clientY - popup.offsetTop
    };
    this.mouseMoveListener = this.renderer.listen('window', 'mousemove', this.onDrag);
    this.mouseUpListener = this.renderer.listen('window', 'mouseup', this.stopDrag);
    this.touchMoveListener = this.renderer.listen('window', 'touchmove', this.onDrag);
    this.touchEndListener = this.renderer.listen('window', 'touchend', this.stopDrag);
  }

  onDrag = (event: MouseEvent | TouchEvent) => {
    if (!this.isDragging) return;
    const popup = this.chatPopupRef.nativeElement;
    const clientX = (event instanceof TouchEvent) ? event.touches[0].clientX : event.clientX;
    const clientY = (event instanceof TouchEvent) ? event.touches[0].clientY : event.clientY;
    let left = clientX - this.dragOffset.x;
    let top = clientY - this.dragOffset.y;
    left = Math.max(0, Math.min(window.innerWidth - popup.offsetWidth, left));
    top = Math.max(0, Math.min(window.innerHeight - popup.offsetHeight, top));
    popup.style.left = left + 'px';
    popup.style.top = top + 'px';
  };

  stopDrag = () => {
    this.isDragging = false;
    this.destroyDrag();
  };

  startResize(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this.isResizing = true;
    const popup = this.chatPopupRef.nativeElement;
    const clientX = (event instanceof TouchEvent) ? event.touches[0].clientX : event.clientX;
    const clientY = (event instanceof TouchEvent) ? event.touches[0].clientY : event.clientY;
    this.resizeStart = {
      x: clientX,
      y: clientY,
      width: popup.offsetWidth,
      height: popup.offsetHeight
    };
    this.resizeMoveListener = this.renderer.listen('window', 'mousemove', this.onResize);
    this.resizeUpListener = this.renderer.listen('window', 'mouseup', this.stopResize);
    this.resizeTouchMoveListener = this.renderer.listen('window', 'touchmove', this.onResize);
    this.resizeTouchEndListener = this.renderer.listen('window', 'touchend', this.stopResize);
  }

  onResize = (event: MouseEvent | TouchEvent) => {
    if (!this.isResizing) return;
    const popup = this.chatPopupRef.nativeElement;
    const clientX = (event instanceof TouchEvent) ? event.touches[0].clientX : event.clientX;
    const clientY = (event instanceof TouchEvent) ? event.touches[0].clientY : event.clientY;

    let newWidth = this.resizeStart.width + (clientX - this.resizeStart.x);
    let newHeight = this.resizeStart.height + (clientY - this.resizeStart.y);

    popup.style.width = Math.max(250, newWidth) + 'px';
    popup.style.height = Math.max(300, newHeight) + 'px';
  };

  stopResize = () => {
    this.isResizing = false;
    this.destroyResize();
  };

  destroyResize() {
    if (this.resizeMoveListener) { this.resizeMoveListener(); this.resizeMoveListener = null; }
    if (this.resizeUpListener) { this.resizeUpListener(); this.resizeUpListener = null; }
    if (this.resizeTouchMoveListener) { this.resizeTouchMoveListener(); this.resizeTouchMoveListener = null; }
    if (this.resizeTouchEndListener) { this.resizeTouchEndListener(); this.resizeTouchEndListener = null; }
  }

  destroyDrag() {
    if (this.mouseMoveListener) { this.mouseMoveListener(); this.mouseMoveListener = null; }
    if (this.mouseUpListener) { this.mouseUpListener(); this.mouseUpListener = null; }
    if (this.touchMoveListener) { this.touchMoveListener(); this.touchMoveListener = null; }
    if (this.touchEndListener) { this.touchEndListener(); this.touchEndListener = null; }
  }
}